import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikHelpers,
  useField,
} from 'formik';
import TextField from 'hellospa/foundation/control/text-field';
import ErrorMessage from 'hellospa/components/error-message';
import HelloModal from 'common/components/hello-modal';
import TooltipUI, {
  ArrowDirection,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import * as types from 'hellospa/page/prep-and-send/data/types';
import styles from './index.scss';
import messages from './intl';
import AccessCodeIcon from 'hellospa/icons/access-code-icon';
import classNames from 'classnames';

interface InitialValues {
  accessCode: string,
}

type Props = {
  parent: string,
  accessCode: string,
  onClose: (accessCode: string) => void,
};

const AccessCodeFields = () => {
  const intl = useIntl();

  const [, meta] = useField<InitialValues['accessCode']>('accessCode');

  return (
    <div className={classNames(styles.modalFields, styles.fields)}>
      <div
        className={classNames({
          [styles.errorFieldContainer]: meta.touched && meta.error,
        })}
      >
        <div className={styles.modalBody}>
          {intl.formatMessage(messages.fieldAccessCodeBody)}
        </div>
        <label className={styles.label} htmlFor="accessCode">
          {intl.formatMessage(messages.accessCodeLabel)}
        </label>
        <Field
          name="accessCode"
        >
          {({ field }: FieldProps) => <TextField
            id="accessCode"
            role="accessCode"
            data-qa-ref="accessCode"
            {...field} />}
        </Field>
        <div
          className={styles.errorContainer}
          role="accessCode-error"
          data-qa-ref="accessCode-error"
        >
          <ErrorMessage
            name="accessCode"
            className={styles.error} />
        </div>
      </div>
    </div>
  );
};

const AccessCode = (props: Props) => {
  const {
    parent,
    onClose,
  } = props;

  const intl = useIntl();

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  /**
   * Save + Close. Prevent closing if there is a validation error
   * Have to use validateForm() as user/test may click modal close button
   * before Formik has finished validation process
   */
  const close = async (
    values: InitialValues,
    validateForm: FormikHelpers<InitialValues>['validateForm'],
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length) {
      return;
    }

    onClose(values.accessCode);
    closeModal();
  };

  const initialValues: InitialValues = {
    accessCode: props.accessCode,
  };

  const schema = Yup.object<InitialValues>({
    accessCode: types.accessCodeModalSchema,
  });
  const hasAccessCode = props.accessCode.length > 0;

  return (
    <React.Fragment>
      <Button
        type="button"
        kind={ButtonType.Subtle}
        onClick={openModal}
        data-qa-ref={`${parent}.buttons.accessCode.open`}
      >
        <AccessCodeIcon
          locked={hasAccessCode}
          className={classNames(styles.accessCodeIcon, {
            [styles.active]: hasAccessCode,
          })} />

        <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
          {intl.formatMessage(messages.accessCodeTooltip)}
        </TooltipUI>
      </Button>

      {modalOpen && (
        <Formik
          initialValues={initialValues}
          onSubmit={async (_values) => { }}
          validateOnChange={false}
          validationSchema={schema}
          // eslint-disable-next-line react/no-children-prop
          children={({ values, handleChange, validateForm }) => (
            <HelloModal
              contentLabel={intl.formatMessage(messages.accessCodeTitle)}
              width="540px"
              height="75vh"
              scrollable={false}
              isOpen={true}
              onRequestClose={closeModal}
              buttons={[{
                type: 'primary',
                text: intl.formatMessage(messages.accessCodeSave),
                'data-qa-ref': 'recipients.buttons.accessCode.close',
                onClick: async () => close(values, validateForm),
              }]}
            >
              <Form
                onChange={(e) => {
                  // Prevents parent's onChange from triggering,
                  // which causes loss of field focus
                  e.stopPropagation();
                  return handleChange;
                }}
              >
                <AccessCodeFields />
              </Form>
            </HelloModal>
          )} />
      )}
    </React.Fragment>
  );
};

export default AccessCode;
